import { useCallback, useEffect, useState } from "react";
import { History, Pencil, Plus, RotateCcw, Trash2, FlaskConical, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { SelectField } from "../../components/site/FormBits";
import { FIELD_LABELS } from "../../data/options";
import { api, apiError } from "../../lib/api";
import { RuleEditor, optionsFor } from "./RuleEditor";

const condText = (c) => {
  const labels = c.values.map((v) => optionsFor(c.field).find((o) => o.value === v)?.label || v);
  return `${FIELD_LABELS[c.field]} ${c.op === "in" ? "∈" : "∉"} ${labels.join(", ")}`;
};

const RuleCard = ({ r, forfaitName, onEdit, onToggle, onDelete }) => (
  <div className="rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB] p-5" data-testid={`rule-card-${r.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-widest text-[#242020]/50">Priorité {r.priority}</p>
        <h3 className="mt-1 text-lg">{r.name}</h3>
        <p className="mt-1 text-sm">→ <strong className="text-[#650D23]">{forfaitName(r.forfait_slug)}</strong></p>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <label className="flex items-center gap-2"><Switch checked={r.enabled} onCheckedChange={(v) => onToggle(r, { enabled: v })} data-testid={`rule-${r.id}-enabled`} />Activée</label>
        <label className="flex items-center gap-2"><Switch checked={r.approved} onCheckedChange={(v) => onToggle(r, { approved: v })} data-testid={`rule-${r.id}-approved`} />Approuvée</label>
        <button onClick={() => onEdit(r)} aria-label="Modifier" data-testid={`rule-${r.id}-edit`}><Pencil className="h-4 w-4 text-[#650D23]" /></button>
        <button onClick={() => onDelete(r)} aria-label="Supprimer" data-testid={`rule-${r.id}-delete`}><Trash2 className="h-4 w-4 text-[#b42318]" /></button>
      </div>
    </div>
    <ul className="mt-3 space-y-1 text-sm text-[#242020]/75">{r.conditions.map((c, i) => <li key={i}>• {condText(c)}</li>)}</ul>
    {!r.approved && <span className="mt-3 inline-block rounded-full bg-[#C97A14]/10 px-2.5 py-1 text-xs text-[#8a530c]">Brouillon — non utilisée sur le site public</span>}
  </div>
);

const Simulator = ({ forfaitName }) => {
  const [a, setA] = useState({ statut: "autonome", anciennete: "moins2", benefice: "moins5k", employes: "non", objectif: "proteger", secteur: "esthetique-et-beaute", metier: "estheticienne" });
  const [drafts, setDrafts] = useState(true);
  const [res, setRes] = useState(null);
  const run = async () => {
    try { setRes((await api.post("/admin/rules/simulate", { answers: a, include_drafts: drafts })).data); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB] p-5" data-testid="rules-simulator">
      <h3 className="flex items-center gap-2 text-lg"><FlaskConical className="h-5 w-5 text-[#650D23]" />Tester une combinaison</h3>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {Object.keys(FIELD_LABELS).map((k) => (
          <div key={k}><p className="mb-1 text-xs text-[#242020]/60">{FIELD_LABELS[k]}</p>
            <SelectField value={a[k]} onChange={(v) => setA((p) => ({ ...p, [k]: v }))} options={optionsFor(k)} testid={`sim-${k}`} /></div>
        ))}
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm"><Checkbox checked={drafts} onCheckedChange={(v) => setDrafts(v === true)} data-testid="sim-include-drafts" />Inclure les règles non approuvées (aperçu)</label>
      <button onClick={run} className="btn-primary mt-4 !py-2.5" data-testid="sim-run">Prévisualiser le résultat</button>
      {res && (
        <div className="mt-4 rounded-xl bg-[#FAF6F1] p-4 text-sm" data-testid="sim-result">
          <p><strong>Forfait :</strong> <span data-testid="sim-result-forfait">{res.forfait_slug ? forfaitName(res.forfait_slug) : "Aucun — message de consultation"}</span></p>
          <p className="mt-1"><strong>Règle :</strong> {res.rule_name || "—"} {res.matched_rule_ids.length > 1 && `(${res.matched_rule_ids.length} règles correspondaient)`}</p>
          <p className="mt-2 text-[#242020]/75">{res.explanation}</p>
        </div>
      )}
    </div>
  );
};

const Versions = ({ versions, onRestore }) => (
  <div className="rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB] p-5" data-testid="rules-versions">
    <h3 className="flex items-center gap-2 text-lg"><History className="h-5 w-5 text-[#650D23]" />Historique des versions</h3>
    <ul className="mt-4 max-h-80 space-y-2 overflow-y-auto text-sm">
      {versions.map((v) => (
        <li key={v.version} className="flex items-center justify-between gap-3 border-b border-[#EDE3D9] pb-2" data-testid={`rules-version-${v.version}`}>
          <div><p>v{v.version} — {v.note}</p><p className="text-xs text-[#242020]/55">{new Date(v.created_at).toLocaleString("fr-CA")} · {v.author} · {v.rules.length} règle(s)</p></div>
          <button onClick={() => onRestore(v.version)} className="shrink-0 text-[#650D23]" aria-label="Restaurer" data-testid={`rules-version-${v.version}-restore`}><RotateCcw className="h-4 w-4" /></button>
        </li>
      ))}
    </ul>
  </div>
);

export default function RulesTab({ forfaits, forfaitName }) {
  const [rules, setRules] = useState([]);
  const [versions, setVersions] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(() => {
    api.get("/admin/rules").then((r) => setRules(r.data));
    api.get("/admin/rules/versions").then((r) => setVersions(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r, patch) => {
    const { id, created_at, updated_at, ...body } = { ...r, ...patch };
    try { await api.put(`/admin/rules/${id}`, body); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Supprimer la règle « ${r.name} »?`)) return;
    await api.delete(`/admin/rules/${r.id}`); load();
  };
  const restore = async (v) => {
    if (!window.confirm(`Restaurer la version ${v}?`)) return;
    await api.post(`/admin/rules/versions/${v}/restore`); toast.success("Version restaurée"); load();
  };
  const approved = rules.filter((r) => r.enabled && r.approved).length;

  return (
    <div className="space-y-6" data-testid="rules-tab">
      {approved === 0 && (
        <p className="flex items-start gap-3 rounded-2xl border border-[#C97A14]/30 bg-[#C97A14]/10 p-4 text-sm text-[#6b4109]" data-testid="rules-no-approved-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />Aucune règle n'est approuvée : le site public affiche « Une consultation permettra de déterminer les solutions adaptées à votre situation » à tous les visiteurs.
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          <div className="flex items-center justify-between"><h2 className="text-2xl">Règles ({rules.length})</h2>
            <button onClick={() => setEditing(null)} className="btn-primary !py-2.5" data-testid="rule-new-button"><Plus className="h-4 w-4" />Nouvelle règle</button></div>
          {rules.map((r) => <RuleCard key={r.id} r={r} forfaitName={forfaitName} onEdit={setEditing} onToggle={toggle} onDelete={remove} />)}
        </div>
        <div className="space-y-6"><Simulator forfaitName={forfaitName} /><Versions versions={versions} onRestore={restore} /></div>
      </div>
      {editing !== undefined && <RuleEditor rule={editing} forfaits={forfaits} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
